import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Slider } from "@/components/ui/slider"
import { Switch } from "@/components/ui/switch"
import { AnimatedCard } from "./AnimatedCard"
import { GlassCard } from "./GlassCard"
import { TierBadge } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  Brain, 
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Heart,
  Eye,
  Share2,
  MessageSquare,
  ShoppingBag,
  Star,
  Zap,
  RefreshCw,
  Settings,
  X,
  ThumbsUp,
  ThumbsDown,
  Bookmark
} from "lucide-react"

interface CuratedContent {
  id: string
  type: 'product' | 'post' | 'challenge' | 'user' | 'event'
  title: string
  description: string
  image: string
  author?: {
    name: string
    avatar: string
    tier: 'initiate' | 'creator' | 'architect'
  }
  metrics: {
    relevanceScore: number
    engagementPrediction: number
    trending: boolean
    personalizedScore: number
  }
  tags: string[]
  reason: string
  actionLabel: string
  metadata?: any
}

interface CurationSettings {
  personalizedWeight: number
  trendingWeight: number
  diversityWeight: number
  tierPreference: 'all' | 'same' | 'aspirational'
  contentTypes: {
    products: boolean
    posts: boolean
    challenges: boolean
    users: boolean
    events: boolean
  }
  refreshInterval: number
}

interface ContentCuratorProps {
  isOpen: boolean
  onClose: () => void
}

const mockCuratedContent: CuratedContent[] = [
  {
    id: '1',
    type: 'product',
    title: 'Architect Hoodie - Limited Drop',
    description: 'Premium architectural-inspired hoodie with technical fabric blend. Only 47 pieces remaining in your preferred sage colorway.',
    image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=300&h=200&fit=crop',
    metrics: {
      relevanceScore: 96,
      engagementPrediction: 89,
      trending: true,
      personalizedScore: 94
    },
    tags: ['architect-collection', 'limited-edition', 'sage', 'premium'],
    reason: 'Matches your architectural aesthetic preference and sage color choice',
    actionLabel: 'View Product',
    metadata: { price: 220, stock: 47, totalStock: 100 }
  },
  {
    id: '2',
    type: 'post',
    title: 'Maya\'s Morning Movement Routine',
    description: 'See how architect-tier Maya starts her day with intentional movement. Her 15-minute routine perfect for your 7 AM schedule preference.',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&h=200&fit=crop',
    author: {
      name: 'Maya Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=40&h=40&fit=crop',
      tier: 'architect'
    },
    metrics: {
      relevanceScore: 92,
      engagementPrediction: 85,
      trending: false,
      personalizedScore: 88
    },
    tags: ['morning-routine', 'movement', '15-minutes', 'architect'],
    reason: 'Aligns with your morning movement goals and time constraints',
    actionLabel: 'Watch Routine'
  },
  {
    id: '3',
    type: 'challenge',
    title: 'Architectural Aesthetics Challenge',
    description: 'Style your Gameshape pieces in architectural settings. Perfect for your design background and kinetic style preference.',
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=200&fit=crop',
    metrics: {
      relevanceScore: 89,
      engagementPrediction: 91,
      trending: true,
      personalizedScore: 90
    },
    tags: ['architecture', 'styling', 'aesthetic', 'challenge'],
    reason: 'Combines your architectural interests with style challenges',
    actionLabel: 'Join Challenge',
    metadata: { participants: 127, timeLeft: '6 days', reward: 500 }
  },
  {
    id: '4',
    type: 'user',
    title: 'Connect with Jordan Kim',
    description: 'Fellow creator with 87% interest overlap. Recently shared kinetic movement techniques that match your training style.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop',
    author: {
      name: 'Jordan Kim',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop',
      tier: 'creator'
    },
    metrics: {
      relevanceScore: 87,
      engagementPrediction: 82,
      trending: false,
      personalizedScore: 85
    },
    tags: ['creator', 'kinetic-movement', 'training', 'community'],
    reason: '87% shared interests and complementary movement styles',
    actionLabel: 'Follow User'
  },
  {
    id: '5',
    type: 'event',
    title: 'Design Thinking Workshop',
    description: 'Explore how design principles apply to movement and style. Led by architect-tier members, perfect for your creative background.',
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop',
    metrics: {
      relevanceScore: 84,
      engagementPrediction: 88,
      trending: false,
      personalizedScore: 86
    },
    tags: ['design-thinking', 'workshop', 'architect', 'virtual'],
    reason: 'Matches your design background and learning preferences',
    actionLabel: 'Register',
    metadata: { date: 'Feb 15, 2024', time: '2:00 PM EST', attendees: 24, maxAttendees: 30 }
  }
]

export function ContentCurator({ isOpen, onClose }: ContentCuratorProps) {
  const { state } = useApexState()
  const [curatedContent, setCuratedContent] = useState<CuratedContent[]>(mockCuratedContent)
  const [showSettings, setShowSettings] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [settings, setSettings] = useState<CurationSettings>({
    personalizedWeight: 70,
    trendingWeight: 20,
    diversityWeight: 10,
    tierPreference: 'aspirational',
    contentTypes: {
      products: true,
      posts: true,
      challenges: true,
      users: true,
      events: true
    },
    refreshInterval: 30
  })

  const handleRefresh = () => {
    setIsRefreshing(true)
    // Simulate AI recuration
    setTimeout(() => {
      // Shuffle and slightly modify scores for demo
      const shuffled = [...mockCuratedContent]
        .sort(() => Math.random() - 0.5)
        .map(item => ({
          ...item,
          metrics: {
            ...item.metrics,
            personalizedScore: Math.max(70, Math.min(100, item.metrics.personalizedScore + (Math.random() - 0.5) * 10)),
            engagementPrediction: Math.max(70, Math.min(100, item.metrics.engagementPrediction + (Math.random() - 0.5) * 8))
          }
        }))
        .sort((a, b) => b.metrics.personalizedScore - a.metrics.personalizedScore)
      
      setCuratedContent(shuffled)
      setIsRefreshing(false)
    }, 1500)
  }

  const handleContentAction = (content: CuratedContent, action: 'like' | 'dislike' | 'save') => {
    console.log(`${action} content:`, content.id)
    // Update AI learning based on user feedback
  }

  const getTypeIcon = (type: CuratedContent['type']) => {
    switch (type) {
      case 'product': return <ShoppingBag size={16} className="text-primary" />
      case 'post': return <MessageSquare size={16} className="text-blue-400" />
      case 'challenge': return <Target size={16} className="text-accent" />
      case 'user': return <Users size={16} className="text-purple-400" />
      case 'event': return <Star size={16} className="text-green-400" />
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-400'
    if (score >= 80) return 'text-blue-400'
    if (score >= 70) return 'text-yellow-400'
    return 'text-orange-400'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden">
          <GlassCard blur="lg" opacity="high">
            <div className="flex flex-col h-full max-h-[90vh]">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-primary/20 flex items-center justify-center">
                    <Brain size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">AI Content Curator</h2>
                    <p className="text-sm text-muted-foreground">Personalized content recommendations</p>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                    AI Powered
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettings(!showSettings)}
                  >
                    <Settings size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={onClose}>
                    <X size={20} />
                  </Button>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <div className="p-6 border-b border-white/10 bg-muted/5">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-foreground">Algorithm Weights</h4>
                      
                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm text-foreground">Personalization</label>
                            <span className="text-sm text-muted-foreground">{settings.personalizedWeight}%</span>
                          </div>
                          <Slider
                            value={[settings.personalizedWeight]}
                            onValueChange={([value]) => setSettings(prev => ({ ...prev, personalizedWeight: value }))}
                            min={0}
                            max={100}
                            step={5}
                          />
                        </div>
                        
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm text-foreground">Trending</label>
                            <span className="text-sm text-muted-foreground">{settings.trendingWeight}%</span>
                          </div>
                          <Slider
                            value={[settings.trendingWeight]}
                            onValueChange={([value]) => setSettings(prev => ({ ...prev, trendingWeight: value }))}
                            min={0}
                            max={100}
                            step={5}
                          />
                        </div>
                        
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm text-foreground">Diversity</label>
                            <span className="text-sm text-muted-foreground">{settings.diversityWeight}%</span>
                          </div>
                          <Slider
                            value={[settings.diversityWeight]}
                            onValueChange={([value]) => setSettings(prev => ({ ...prev, diversityWeight: value }))}
                            min={0}
                            max={100}
                            step={5}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-foreground">Content Types</h4>
                      <div className="space-y-3">
                        {Object.entries(settings.contentTypes).map(([type, enabled]) => (
                          <div key={type} className="flex items-center justify-between">
                            <label className="text-sm text-foreground capitalize">{type}</label>
                            <Switch
                              checked={enabled}
                              onCheckedChange={(checked) => 
                                setSettings(prev => ({
                                  ...prev,
                                  contentTypes: { ...prev.contentTypes, [type]: checked }
                                }))
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-foreground">Preferences</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="text-sm text-foreground mb-2 block">Tier Preference</label>
                          <select
                            value={settings.tierPreference}
                            onChange={(e) => setSettings(prev => ({ ...prev, tierPreference: e.target.value as any }))}
                            className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                          >
                            <option value="all">All Tiers</option>
                            <option value="same">Same Tier</option>
                            <option value="aspirational">Aspirational</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="text-sm text-foreground mb-2 block">
                            Refresh Interval: {settings.refreshInterval}min
                          </label>
                          <Slider
                            value={[settings.refreshInterval]}
                            onValueChange={([value]) => setSettings(prev => ({ ...prev, refreshInterval: value }))}
                            min={5}
                            max={120}
                            step={5}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Feed */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4">
                  {curatedContent.map(content => (
                    <AnimatedCard key={content.id}>
                      <Card className="bg-card/50 border-white/10">
                        <CardContent className="p-0">
                          <div className="flex gap-4 p-4">
                            {/* Content Image */}
                            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={content.image}
                                alt={content.title}
                                className="w-full h-full object-cover"
                              />
                            </div>

                            {/* Content Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {getTypeIcon(content.type)}
                                  <h3 className="font-medium text-foreground">{content.title}</h3>
                                  {content.metrics.trending && (
                                    <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                                      <TrendingUp size={10} className="mr-1" />
                                      Trending
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-1">
                                  <Sparkles size={12} className="text-primary" />
                                  <span className={`text-sm font-medium ${getScoreColor(content.metrics.personalizedScore)}`}>
                                    {content.metrics.personalizedScore}%
                                  </span>
                                </div>
                              </div>

                              <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
                                {content.description}
                              </p>

                              {/* Author */}
                              {content.author && (
                                <div className="flex items-center gap-2 mb-3">
                                  <Avatar className="w-5 h-5">
                                    <AvatarImage src={content.author.avatar} alt={content.author.name} />
                                    <AvatarFallback className="text-xs">{content.author.name.slice(0, 2)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-xs text-muted-foreground">{content.author.name}</span>
                                  <TierBadge tier={content.author.tier} size="xs" />
                                </div>
                              )}

                              {/* Tags */}
                              <div className="flex flex-wrap gap-1 mb-3">
                                {content.tags.slice(0, 3).map(tag => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                                {content.tags.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{content.tags.length - 3}
                                  </Badge>
                                )}
                              </div>

                              {/* Metrics & Reason */}
                              <div className="space-y-2">
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>Relevance: {content.metrics.relevanceScore}%</span>
                                  <span>Engagement: {content.metrics.engagementPrediction}%</span>
                                </div>
                                
                                <div className="flex items-start gap-2">
                                  <Brain size={12} className="text-purple-400 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-purple-400 leading-relaxed">
                                    {content.reason}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col items-center gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90 text-black"
                              >
                                {content.actionLabel}
                              </Button>
                              
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleContentAction(content, 'like')}
                                  className="p-2"
                                >
                                  <ThumbsUp size={12} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleContentAction(content, 'dislike')}
                                  className="p-2"
                                >
                                  <ThumbsDown size={12} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleContentAction(content, 'save')}
                                  className="p-2"
                                >
                                  <Bookmark size={12} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </AnimatedCard>
                  ))}
                </div>

                {/* Load More */}
                <div className="text-center pt-6">
                  <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                    {isRefreshing ? (
                      <>
                        <RefreshCw size={16} className="mr-2 animate-spin" />
                        Curating Content...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} className="mr-2" />
                        Load More Curated Content
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}